/**
 * ClientPortal — Notion-style Kanban reviewer.
 *
 * Public mode (`/p/:token`): token-based no-auth access. Clients can:
 *   - View posts on a 3-column Kanban (Draft / Reviewed / Published)
 *   - Open a card → Notion-style drawer with full image, formatted caption
 *   - Drag a card between Draft ↔ Reviewed (Published is owner-only)
 *   - "Recommend change" → leaves a comment
 *   - "Submit your draft" → posts an alternative caption to the owner
 *   - "Approve" → marks the post approved (if workspace.approval_enabled)
 *
 * Auth mode (`/client/:workspaceId`): preserved for member preview.
 */
import React, { useEffect, useMemo, useState } from "react";
import { useParams } from "react-router-dom";
import axios from "axios";
import { apiGet, apiPost, apiDelete } from "@/lib/api";
import { toast } from "sonner";
import { VoiceChip, voiceColor } from "@/lib/voiceColors";
import {
  ViewSwitcher, GalleryView, TableView, TimelineView,
} from "@/pages/portal/ContentViews";
import { PortalPerformance, PortalUpdates, PortalBank } from "@/pages/PortalPerformance";
import { Calendar as CalendarPicker } from "@/components/ui/calendar";
import { Popover, PopoverTrigger, PopoverContent } from "@/components/ui/popover";
import {
  Flame,
  CheckCircle2,
  X,
  Send,
  MessageSquarePlus,
  FilePlus2,
  Sparkles,
  Image as ImageIcon,
  FileText,
  Calendar,
  ChevronLeft,
  ChevronRight,
  RotateCcw,
  Copy,
  Check,
  LayoutDashboard,
  ClipboardList,
  Library as LibraryIcon,
  BarChart3,
  Users as UsersIcon,
  Menu,
  Target,
  TrendingUp,
  TrendingDown,
  Trash2,
} from "lucide-react";

const COLUMNS = [
  { key: "draft", label: "Draft", hint: "being written" },
  { key: "review", label: "In review", hint: "ready for your feedback" },
  { key: "published", label: "Published", hint: "live on LinkedIn" },
];

const NAME_KEY = "ll_portal_client_name";

export default function ClientPortal({ mode = "auth" }) {
  const params = useParams();
  const workspaceId = params.workspaceId || null;
  const token = params.token || null;

  const [workspace, setWorkspace] = useState(null);
  const [posts, setPosts] = useState([]);
  const [reports, setReports] = useState(null);
  const [hotLeads, setHotLeads] = useState([]);
  const [loading, setLoading] = useState(true);
  const [errMsg, setErrMsg] = useState(null);
  const [openPostId, setOpenPostId] = useState(null);
  const [voiceProfiles, setVoiceProfiles] = useState([]);
  const [voiceFilter, setVoiceFilter] = useState(() => localStorage.getItem("ll_portal_voice") || "");
  useEffect(() => { localStorage.setItem("ll_portal_voice", voiceFilter || ""); }, [voiceFilter]);
  const [clientName, setClientName] = useState(
    () => (mode === "public" ? localStorage.getItem(NAME_KEY) || "" : "you")
  );
  const [namePromptOpen, setNamePromptOpen] = useState(false);

  const base = process.env.REACT_APP_BACKEND_URL;

  const refresh = async () => {
    try {
      if (mode === "public" && token) {
        const r = await axios.get(`${base}/api/p/${token}`);
        const d = r.data || {};
        setWorkspace(d.workspace || null);
        setPosts(d.posts || []);
        setReports(d.reports || null);
        setHotLeads(d.hot_leads || []);
        try {
          const vp = await axios.get(`${base}/api/p/${token}/voice-profiles`);
          setVoiceProfiles(Array.isArray(vp.data) ? vp.data : []);
        } catch { setVoiceProfiles([]); }
      } else if (workspaceId) {
        const [ws, ps, rep, vp] = await Promise.all([
          apiGet(`/workspaces/${workspaceId}`),
          apiGet(`/workspaces/${workspaceId}/posts`).catch(() => []),
          apiGet(`/workspaces/${workspaceId}/reports/overview`).catch(() => null),
          apiGet(`/workspaces/${workspaceId}/voice_profiles`).catch(() => []),
        ]);
        setWorkspace({ ...ws, approval_enabled: ws.approval_enabled !== false });
        setPosts(ps || []);
        setReports(rep);
        setVoiceProfiles(Array.isArray(vp) ? vp : []);
      }
    } catch (e) {
      if (e?.response?.status === 404) {
        setErrMsg("this link has been revoked or never existed. ask your strategist for a fresh one.");
      } else {
        toast.error("could not load portal");
        setErrMsg("something went wrong loading this portal.");
      }
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { refresh(); /* eslint-disable-next-line */ }, [workspaceId, token, mode]);

  const grouped = useMemo(() => {
    const out = { draft: [], review: [], published: [] };
    for (const p of posts) {
      const k = (p.status || "draft").toLowerCase();
      if (out[k]) out[k].push(p);
    }
    return out;
  }, [posts]);

  const openPost = posts.find((p) => (p.id || p.post_id) === openPostId) || null;

  const ensureName = () => {
    if (mode !== "public") return true;
    if (clientName && clientName.trim().length > 0) return true;
    setNamePromptOpen(true);
    return false;
  };

  // ---- Public actions (only when mode === public) ----
  const postFeedback = async (postId, path, payload) => {
    if (!ensureName()) return null;
    try {
      const r = await axios.post(
        `${base}/api/p/${token}/posts/${postId}/${path}`,
        { author_name: clientName, ...payload }
      );
      await refresh();
      return r.data;
    } catch (e) {
      toast.error(e?.response?.data?.detail || "could not send");
      return null;
    }
  };

  if (loading) {
    return (
      <div className="portal-light min-h-screen">
        <div className="px-10 py-10 text-stone-600">loading…</div>
      </div>
    );
  }
  if (errMsg) {
    return (
      <div className="portal-light min-h-screen flex items-center justify-center">
        <div className="portal-card px-10 py-12 text-center max-w-md">
          <div className="portal-overline mb-3">access</div>
          <h2 className="portal-serif text-3xl text-stone-900 mb-3">link unavailable</h2>
          <p className="text-sm text-stone-600">{errMsg}</p>
        </div>
      </div>
    );
  }

  return (
    <PortalShell
      mode={mode}
      workspace={workspace}
      clientName={clientName}
      onChangeName={() => setNamePromptOpen(true)}
      updatesSlot={<PortalUpdates mode={mode} token={token} workspaceId={workspaceId} />}
      renderScreen={(screen, goto) => (
        <>
          {screen === "overview" && (
            <OverviewScreen
              reports={reports}
              hotLeads={hotLeads}
              grouped={grouped}
              onGoto={goto}
            />
          )}
          {screen === "deliverables" && (
            <DeliverablesScreen mode={mode} token={token} workspaceId={workspaceId} />
          )}
          {screen === "review" && (
            <ContentBoard
              posts={posts}
              mode={mode}
              voiceProfiles={voiceProfiles}
              voiceFilter={voiceFilter}
              onVoiceFilter={setVoiceFilter}
              onOpenPost={(id) => setOpenPostId(id)}
              onDropMove={async (postId, fromStatus, toStatus) => {
                if (mode !== "public") return;
                if (toStatus === "published") {
                  toast.error("only your strategist can mark a post published");
                  return;
                }
                if (toStatus === fromStatus) return;
                setPosts((prev) => prev.map((p) =>
                  (p.id || p.post_id) === postId ? { ...p, status: toStatus } : p
                ));
                try {
                  await axios.post(`${base}/api/p/${token}/posts/${postId}/comment`, {
                    author_name: clientName || "client",
                    content: `[moved to ${toStatus}]`,
                  });
                } catch {
                  setPosts((prev) => prev.map((p) =>
                    (p.id || p.post_id) === postId ? { ...p, status: fromStatus } : p
                  ));
                }
              }}
            />
          )}
          {screen === "bank" && (
            <PortalBank mode={mode} token={token} workspaceId={workspaceId} />
          )}
          {screen === "performance" && (
            <PortalPerformance mode={mode} token={token} workspaceId={workspaceId} />
          )}
          {screen === "outreach" && (
            <OutreachScreen mode={mode} token={token} workspaceId={workspaceId} />
          )}
        </>
      )}
    >
      {/* Drawer */}
      {openPost && (
        <PostDrawer
          post={openPost}
          workspace={workspace}
          mode={mode}
          clientName={clientName}
          onClose={() => setOpenPostId(null)}
          onComment={(content) => postFeedback(openPost.id || openPost.post_id, "comment", { content })}
          onSubmitDraft={(caption, note) => postFeedback(openPost.id || openPost.post_id, "draft-submission", { caption, note })}
          onApprove={() => postFeedback(openPost.id || openPost.post_id, "approve", {})}
          onRequireName={() => setNamePromptOpen(true)}
        />
      )}

      {namePromptOpen && (
        <NamePrompt
          current={clientName}
          onSave={(n) => {
            setClientName(n);
            localStorage.setItem(NAME_KEY, n);
            setNamePromptOpen(false);
          }}
          onCancel={() => setNamePromptOpen(false)}
        />
      )}
    </PortalShell>
  );
}

// ---------------------------------------------------------------------------
// App shell — persistent sidebar + fixed top bar + single scrolling content area
// ---------------------------------------------------------------------------
const SCREEN_KEY = "ll_portal_active_screen";
const SIDEBAR_COLLAPSED_KEY = "ll_portal_sidebar_collapsed";
const NAV = [
  { key: "overview",    label: "Overview",    Icon: LayoutDashboard },
  { key: "deliverables", label: "Deliverables", Icon: Target },
  { key: "review",      label: "Review",      Icon: ClipboardList },
  { key: "bank",        label: "Bank",        Icon: LibraryIcon },
  { key: "performance", label: "Performance", Icon: BarChart3 },
  { key: "outreach",    label: "Outreach",    Icon: UsersIcon },
];

function PortalShell({ mode, workspace, clientName, onChangeName, updatesSlot, renderScreen, children }) {
  const [screen, setScreen] = useState(() => localStorage.getItem(SCREEN_KEY) || "overview");
  const [collapsed, setCollapsed] = useState(() => localStorage.getItem(SIDEBAR_COLLAPSED_KEY) === "1");
  const [mobileOpen, setMobileOpen] = useState(false);

  useEffect(() => { localStorage.setItem(SCREEN_KEY, screen); }, [screen]);
  useEffect(() => { localStorage.setItem(SIDEBAR_COLLAPSED_KEY, collapsed ? "1" : "0"); }, [collapsed]);

  const goto = (k) => { setScreen(k); setMobileOpen(false); };
  const activeMeta = NAV.find((n) => n.key === screen) || NAV[0];

  return (
    <div className="portal-light min-h-screen flex" data-testid="portal-shell">
      {/* Sidebar — desktop */}
      <aside
        className={`hidden md:flex fixed inset-y-0 left-0 z-40 flex-col bg-portal-cream/60 backdrop-blur transition-[width] duration-200 ${
          collapsed ? "w-[68px]" : "w-[232px]"
        }`}
        data-testid="portal-sidebar"
      >
        <SidebarBrand workspace={workspace} collapsed={collapsed} />
        <nav className="flex-1 px-3 py-4 space-y-1">
          {NAV.map((n) => (
            <NavItem
              key={n.key}
              item={n}
              active={screen === n.key}
              collapsed={collapsed}
              onClick={() => goto(n.key)}
            />
          ))}
        </nav>
        <button
          onClick={() => setCollapsed((c) => !c)}
          className="mx-3 mb-4 portal-btn portal-btn-ghost inline-flex items-center justify-center gap-2 text-[11px]"
          data-testid="portal-sidebar-collapse"
        >
          {collapsed
            ? <ChevronRight className="w-3.5 h-3.5" />
            : <><ChevronLeft className="w-3.5 h-3.5" /> collapse</>}
        </button>
      </aside>

      {/* Sidebar — mobile drawer */}
      {mobileOpen && (
        <>
          <div className="fixed inset-0 z-40 bg-black/30 md:hidden" onClick={() => setMobileOpen(false)} />
          <aside className="fixed inset-y-0 left-0 z-50 w-[240px] flex flex-col bg-portal-cream md:hidden">
            <SidebarBrand workspace={workspace} collapsed={false} />
            <nav className="flex-1 px-3 py-4 space-y-1">
              {NAV.map((n) => (
                <NavItem key={n.key} item={n} active={screen === n.key} collapsed={false} onClick={() => goto(n.key)} />
              ))}
            </nav>
          </aside>
        </>
      )}

      {/* Main pane */}
      <div className={`flex-1 min-w-0 ${collapsed ? "md:pl-[68px]" : "md:pl-[232px]"}`}>
        {/* Fixed top bar — glass, no divider */}
        <header
          className={`ed-topbar fixed top-0 right-0 left-0 z-30 flex items-end justify-between h-20 px-6 md:px-10 pt-6 pb-4 transition-[left] duration-200 ${
            collapsed ? "md:left-[68px]" : "md:left-[232px]"
          }`}
          data-testid="portal-topbar"
        >
          <div className="flex items-end gap-4 min-w-0">
            <button
              className="md:hidden portal-btn portal-btn-ghost p-2 self-center"
              onClick={() => setMobileOpen(true)}
              data-testid="portal-mobile-menu"
              aria-label="Open menu"
            >
              <Menu className="w-4 h-4" />
            </button>
            <div>
              <div className="chapter-eyebrow flex items-center gap-2 mb-1">
                <activeMeta.Icon className="w-3 h-3" strokeWidth={2.5} />
                Portal
              </div>
              <h1
                className="chapter-title truncate"
                style={{ fontSize: "clamp(28px, 4vw, 40px)" }}
                data-testid="portal-screen-title"
              >
                {activeMeta.label}
              </h1>
            </div>
          </div>
          <div className="flex items-center gap-3">
            {updatesSlot}
            <button
              onClick={onChangeName}
              className="portal-btn portal-btn-ghost inline-flex items-center gap-2 max-w-[220px]"
              data-testid="portal-signed-in-as"
              title="Change name"
            >
              <span className="mono text-[10px] uppercase tracking-wider text-stone-500 hidden lg:inline">signed in as</span>
              <span className="text-sm text-stone-900 truncate">{clientName || "set your name"}</span>
            </button>
          </div>
        </header>

        <main className="pt-24" data-testid="portal-main">
          <div className="max-w-7xl mx-auto px-6 md:px-10 py-8 md:py-12">
            {renderScreen ? renderScreen(screen, goto) : null}
          </div>
        </main>
      </div>
      {children}
    </div>
  );
}

function SidebarBrand({ workspace, collapsed }) {
  return (
    <div className={`h-16 flex items-center ${collapsed ? "px-3 justify-center" : "px-5"}`}>
      {collapsed ? (
        <img src="/owlbert/main.png" alt="Owlbert" className="w-8 h-8 object-contain shrink-0" data-testid="portal-sidebar-owlbert" />
      ) : (
        <div data-testid="portal-sidebar-owlbert" className="flex items-center gap-2.5">
          <img src="/owlbert/main.png" alt="Owlbert" className="w-8 h-8 object-contain shrink-0" />
          <div className="min-w-0">
            <div
              className="text-[14px] font-semibold text-stone-900 leading-none tracking-tight truncate"
              style={{ fontFamily: "Manrope, sans-serif" }}
            >
              LinkedIn Lab
            </div>
            <div className="text-[9px] mt-1 font-bold tracking-[0.18em] uppercase text-stone-500 truncate">
              {workspace?.name ? `for ${workspace.name}` : "Client portal"}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function NavItem({ item, active, collapsed, onClick }) {
  const { key, label, Icon } = item;
  return (
    <button
      onClick={onClick}
      data-testid={`portal-nav-${key}`}
      className={`ed-nav-item ${active ? "active" : ""} ${collapsed ? "justify-center" : ""}`}
      title={collapsed ? label : undefined}
    >
      <Icon className="w-4 h-4 shrink-0" strokeWidth={active ? 2.25 : 2} />
      {!collapsed && <span>{label}</span>}
    </button>
  );
}

// ---------------------------------------------------------------------------
// Overview — editorial layout: hero stat + 3 whitespace-driven tiles.
// ---------------------------------------------------------------------------
function OverviewScreen({ reports, hotLeads, grouped, onGoto }) {
  const dv = reports?.deliverable || null;
  const target = dv?.effective_quota ?? reports?.target_monthly_posts ?? 12;
  const delivered = dv?.delivered ?? reports?.posts_published_30d ?? reports?.summary?.posts ?? grouped.published.length;
  const pct = dv?.pct ?? (target > 0 ? Math.min(100, Math.round((delivered / target) * 100)) : 0);
  const ahead = dv ? dv.balance >= 0 : delivered >= target;
  const behindBy = dv ? Math.max(-dv.balance, 0) : Math.max(target - delivered, 0);

  return (
    <div data-testid="overview-screen" className="editorial">
      {/* HERO — current-cycle deliverables, no card wrapper */}
      <button
        onClick={() => onGoto("deliverables")}
        className="w-full text-left group"
        data-testid="overview-target-card"
      >
        <div className="chapter-eyebrow mb-4">
          {dv ? `This cycle · ${fmtDayLabel(dv.period_start)} – ${fmtDayLabel(dv.period_end)}` : "This month"}
        </div>
        <div className="flex items-end gap-6 flex-wrap">
          <span
            className="stat-num stat-num-xl group-hover:text-[#4f46e5] transition-colors"
            data-testid="overview-delivered"
          >
            {delivered}
          </span>
          <div className="pb-2">
            <div className="stat-label mb-1">Delivered / Committed</div>
            <div className="text-sm text-[#4b5563]">
              <span className="font-semibold text-[#111827]">{pct}%</span> of {target} posts
            </div>
          </div>
          {dv && (
            <div className="pb-2 flex items-center gap-3">
              <span
                className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-semibold ${
                  ahead ? "bg-[#ecfdf5] text-[#059669]" : "bg-[#fef2f2] text-[#dc2626]"
                }`}
                data-testid="overview-cycle-status"
              >
                {ahead ? <TrendingUp className="w-3.5 h-3.5" /> : <TrendingDown className="w-3.5 h-3.5" />}
                {ahead ? "On track" : `${behindBy} behind`}
              </span>
              {dv.is_current && (
                <span className="text-xs text-[#6b7280]">{dv.days_remaining} day{dv.days_remaining === 1 ? "" : "s"} left</span>
              )}
            </div>
          )}
        </div>
        <div className="mt-6 h-1.5 rounded-full bg-[#f3f4f6] overflow-hidden max-w-3xl">
          <div
            className="h-full transition-all duration-500"
            style={{ width: `${pct}%`, background: ahead ? "#059669" : "#4F46E5" }}
          />
        </div>
      </button>

      {/* 3 tiles — borderless, breathing whitespace */}
      <div className="ed-section grid grid-cols-1 md:grid-cols-3 gap-8">
        <OverviewTile
          testid="overview-tile-review"
          onClick={() => onGoto("review")}
          overline="Needs your attention"
          value={grouped.review.length}
          label={grouped.review.length === 1 ? "post in review" : "posts in review"}
          Icon={ClipboardList}
          accent={grouped.review.length > 0}
        />
        <OverviewTile
          testid="overview-tile-performance"
          onClick={() => onGoto("performance")}
          overline="Performance"
          value={grouped.published.length}
          label={grouped.published.length === 1 ? "post live" : "posts live"}
          Icon={BarChart3}
        />
        <OverviewTile
          testid="overview-tile-outreach"
          onClick={() => onGoto("outreach")}
          overline="Outreach"
          value={hotLeads.length}
          label={hotLeads.length === 1 ? "hot lead" : "hot leads"}
          Icon={UsersIcon}
          accent={hotLeads.length > 0}
        />
      </div>
    </div>
  );
}

function OverviewTile({ overline, value, label, Icon, onClick, accent, testid }) {
  return (
    <button
      onClick={onClick}
      className="ed-card ed-card-hover text-left flex flex-col gap-5"
      data-testid={testid}
    >
      <div className="flex items-center justify-between">
        <div className="stat-label">{overline}</div>
        <Icon
          className="w-4 h-4"
          strokeWidth={2}
          style={{ color: accent ? "#4f46e5" : "#9ca3af" }}
        />
      </div>
      <div>
        <div
          className="stat-num stat-num-md"
          style={{ color: accent ? "#4f46e5" : "#111827" }}
        >
          {value}
        </div>
        <div className="text-sm text-[#6b7280] mt-2">{label}</div>
      </div>
    </button>
  );
}

// ---------------------------------------------------------------------------
// Deliverables — current-cycle contract tracker + navigable calendar
// (delivered days = green, logged missed days = red w/ reason). Operators
// (auth mode) can log/clear missed days; clients see it read-only.
// ---------------------------------------------------------------------------
function fmtDayLabel(iso) {
  if (!iso) return "";
  try {
    return new Date(iso).toLocaleDateString(undefined, {
      month: "short", day: "numeric", timeZone: "UTC",
    });
  } catch { return String(iso).slice(0, 10); }
}

function fmtFullDate(iso) {
  if (!iso) return "";
  try {
    return new Date(iso).toLocaleDateString(undefined, {
      month: "short", day: "numeric", year: "numeric", timeZone: "UTC",
    });
  } catch { return String(iso).slice(0, 10); }
}

function DeliverablesScreen({ mode, token, workspaceId }) {
  const base = process.env.REACT_APP_BACKEND_URL;
  const canEdit = mode !== "public";
  const [periodIndex, setPeriodIndex] = useState(null); // null = current
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  const load = async (idx) => {
    setLoading(true);
    try {
      let d;
      if (mode === "public" && token) {
        const qs = idx == null ? "" : `?period_index=${idx}`;
        const r = await axios.get(`${base}/api/p/${token}/deliverable/calendar${qs}`);
        d = r.data;
      } else {
        d = await apiGet(
          `/workspaces/${workspaceId}/deliverable/calendar`,
          idx == null ? undefined : { period_index: idx }
        );
      }
      setData(d || null);
      if (idx == null && d?.summary) setPeriodIndex(d.summary.period_index);
    } catch {
      setData(null);
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { load(periodIndex); /* eslint-disable-next-line */ }, [workspaceId, token, mode]);

  const s = data?.summary || null;
  const go = (delta) => {
    const cur = s?.period_index ?? periodIndex ?? 0;
    const next = cur + delta;
    if (delta < 0 && !s?.has_prev) return;
    if (delta > 0 && !s?.has_next) return;
    setPeriodIndex(next);
    load(next);
  };

  const logMissed = async (date) => {
    const reason = window.prompt(`Log a missed day — ${fmtDayLabel(date)}\n\nReason (the client will see this):`);
    if (!reason || !reason.trim()) return;
    try {
      await apiPost(`/workspaces/${workspaceId}/deliverable/logs`, {
        date, reason: reason.trim(), contract_id: s?.contract_id,
      });
      toast.success("Missed day logged");
      load(periodIndex);
    } catch {
      toast.error("Could not save");
    }
  };
  const clearMissed = async (logId) => {
    try {
      await apiDelete(`/workspaces/${workspaceId}/deliverable/logs/${logId}`);
      load(periodIndex);
    } catch {
      toast.error("Could not remove");
    }
  };

  if (loading) {
    return <div className="editorial text-sm text-[#6b7280]" data-testid="deliverables-loading">loading deliverables…</div>;
  }
  if (!s) {
    return (
      <div className="editorial" data-testid="deliverables-empty">
        <div className="chapter-eyebrow mb-4">Deliverables</div>
        <div className="ed-card max-w-xl">
          <div className="stat-num stat-num-md text-[#111827]">No active contract</div>
          <div className="text-sm text-[#6b7280] mt-2">
            Once a delivery agreement is set up, this cycle's committed posts, progress and
            calendar will appear here.
          </div>
        </div>
      </div>
    );
  }

  const ahead = s.balance >= 0;
  const behindBy = Math.max(-s.balance, 0);
  const days = data.days || [];
  // leading blanks so day 1 lands on the right weekday (UTC)
  const firstWeekday = days.length ? new Date(days[0].date + "T00:00:00Z").getUTCDay() : 0;

  return (
    <div className="editorial" data-testid="deliverables-screen">
      {/* Contract summary */}
      <div className="ed-card mb-6" data-testid="deliverables-contract-info">
        <div className="flex items-start justify-between flex-wrap gap-4">
          <div>
            <div className="stat-label">Contract</div>
            <div className="portal-serif text-xl text-[#111827] mt-1">{s.contract_name}</div>
            <div className="text-sm text-[#6b7280] mt-1">
              {s.cadence}{s.client_name ? ` · ${s.client_name}` : ""}
            </div>
          </div>
          <div className="text-right">
            <div className="stat-label">{s.cycle_number >= 1 ? `Cycle ${s.cycle_number}` : "Earlier period"}</div>
            <div className="text-sm text-[#111827] mt-1">
              {fmtDayLabel(s.period_start)} – {fmtDayLabel(s.period_end)}
            </div>
            <div className="text-xs text-[#9ca3af] mt-0.5">contract since {fmtFullDate(s.start_date)}</div>
          </div>
        </div>
      </div>

      {/* Cycle header + navigation */}
      <div className="flex items-center justify-between flex-wrap gap-3 mb-6">
        <div>
          <div className="chapter-eyebrow mb-2">
            {s.is_current ? "Current cycle" : "Past cycle"}
          </div>
          <h2 className="portal-serif text-2xl text-[#111827]">
            {fmtDayLabel(s.period_start)} – {fmtDayLabel(s.period_end)}
          </h2>
        </div>
        <div className="flex items-center gap-2">
          <button
            onClick={() => go(-1)}
            disabled={!s.has_prev}
            className="portal-btn portal-btn-ghost inline-flex items-center gap-1 text-xs disabled:opacity-40"
            data-testid="deliverables-prev-cycle"
          >
            <ChevronLeft className="w-4 h-4" /> Previous
          </button>
          <button
            onClick={() => go(1)}
            disabled={!s.has_next}
            className="portal-btn portal-btn-ghost inline-flex items-center gap-1 text-xs disabled:opacity-40"
            data-testid="deliverables-next-cycle"
          >
            Next <ChevronRight className="w-4 h-4" />
          </button>
        </div>
      </div>

      {/* Stat row */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-6 mb-6">
        <div className="ed-card" data-testid="deliverables-delivered">
          <div className="stat-label">Delivered</div>
          <div className="stat-num stat-num-md mt-2 text-[#111827]">
            {s.delivered}<span className="text-lg text-[#9ca3af]"> / {s.effective_quota}</span>
          </div>
        </div>
        <div className="ed-card" data-testid="deliverables-remaining">
          <div className="stat-label">Remaining</div>
          <div className="stat-num stat-num-md mt-2 text-[#111827]">{s.remaining}</div>
        </div>
        <div className="ed-card" data-testid="deliverables-status">
          <div className="stat-label">Status</div>
          <div className={`stat-num stat-num-md mt-2 ${ahead ? "text-[#059669]" : "text-[#dc2626]"}`}>
            {ahead ? (s.surplus > 0 ? `+${s.surplus} ahead` : "On track") : `${behindBy} behind`}
          </div>
        </div>
        <div className="ed-card" data-testid="deliverables-daysleft">
          <div className="stat-label">{s.is_current ? "Days left" : "Cycle length"}</div>
          <div className="stat-num stat-num-md mt-2 text-[#111827]">
            {s.is_current ? s.days_remaining : s.period_days}
          </div>
        </div>
      </div>

      {s.rollover_in > 0 && (
        <div className="mb-6 text-sm text-[#b45309] bg-[#fffbeb] rounded-lg px-4 py-3" data-testid="deliverables-rollover">
          {s.rollover_in} make-up {s.rollover_in === 1 ? "post" : "posts"} carried over from the previous cycle.
        </div>
      )}

      {/* Progress bar */}
      <div className="mb-8">
        <div className="h-2 rounded-full bg-[#f3f4f6] overflow-hidden max-w-3xl">
          <div className="h-full transition-all duration-500"
            style={{ width: `${s.pct}%`, background: ahead ? "#059669" : "#4F46E5" }} />
        </div>
        <div className="text-xs text-[#6b7280] mt-2">{s.pct}% of committed posts delivered</div>
      </div>

      {/* Calendar */}
      <div className="chapter-eyebrow mb-3">Cycle calendar</div>
      <div className="ed-card" data-testid="deliverables-calendar">
        <div className="grid grid-cols-7 gap-1.5 mb-2">
          {["S", "M", "T", "W", "T", "F", "S"].map((d, i) => (
            <div key={i} className="text-[11px] text-[#9ca3af] text-center font-medium">{d}</div>
          ))}
        </div>
        <div className="grid grid-cols-7 gap-1.5">
          {Array.from({ length: firstWeekday }).map((_, i) => <div key={`b${i}`} />)}
          {days.map((day) => {
            const dayNum = parseInt(day.date.slice(8, 10), 10);
            const isDelivered = day.delivered > 0;
            const isMissed = !!day.missed_reason;
            let cls = "bg-[#f9fafb] text-[#9ca3af]";
            if (isDelivered) cls = "bg-[#ecfdf5] text-[#059669] font-semibold";
            else if (isMissed) cls = "bg-[#fef2f2] text-[#dc2626] font-semibold";
            const clickable = canEdit && !isDelivered;
            return (
              <div
                key={day.date}
                title={isMissed ? day.missed_reason : (isDelivered ? `${day.delivered} delivered` : "")}
                onClick={() => {
                  if (!canEdit || isDelivered) return;
                  if (isMissed) { if (window.confirm("Remove this missed-day note?")) clearMissed(day.log_id); }
                  else logMissed(day.date);
                }}
                className={`aspect-square rounded-md flex flex-col items-center justify-center text-xs relative ${cls} ${clickable ? "cursor-pointer hover:ring-1 hover:ring-[#4f46e5]" : ""}`}
                data-testid={`deliverables-day-${day.date}`}
              >
                <span>{dayNum}</span>
                {isDelivered && <span className="text-[9px] leading-none mt-0.5">{day.delivered}★</span>}
                {isMissed && <span className="text-[9px] leading-none mt-0.5">missed</span>}
              </div>
            );
          })}
        </div>
        {/* Legend */}
        <div className="flex items-center gap-4 mt-4 text-[11px] text-[#6b7280]">
          <span className="inline-flex items-center gap-1.5"><span className="w-3 h-3 rounded bg-[#ecfdf5] ring-1 ring-[#a7f3d0]" /> delivered</span>
          <span className="inline-flex items-center gap-1.5"><span className="w-3 h-3 rounded bg-[#fef2f2] ring-1 ring-[#fecaca]" /> missed</span>
          {canEdit && <span className="ml-auto">tap an empty day to log a missed day with a reason</span>}
        </div>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Outreach — read-only kanban view (mirrors internal Outreach.jsx layout)
// ---------------------------------------------------------------------------
function OutreachScreen({ mode, token, workspaceId }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [openContact, setOpenContact] = useState(null);
  const base = process.env.REACT_APP_BACKEND_URL;

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        let payload;
        if (mode === "public" && token) {
          const r = await axios.get(`${base}/api/p/${token}/outreach/contacts`);
          payload = r.data;
        } else if (workspaceId) {
          const [cs, labels, stages] = await Promise.all([
            apiGet(`/workspaces/${workspaceId}/outreach/contacts`, { include_closed: false }).catch(() => []),
            apiGet(`/workspaces/${workspaceId}/outreach/labels`).catch(() => []),
            apiGet(`/workspaces/${workspaceId}/outreach/stages`).catch(() => []),
          ]);
          payload = { contacts: cs || [], labels: labels || [], stages: stages || [] };
        }
        if (!cancelled) setData(payload || { contacts: [], labels: [], stages: [] });
      } catch {
        if (!cancelled) setData({ contacts: [], labels: [], stages: [] });
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [mode, token, workspaceId, base]);

  if (loading) return <div className="text-center text-sm text-[#9ca3af] py-16" data-testid="outreach-loading">Loading outreach…</div>;
  const contacts = data?.contacts || [];
  const labels = data?.labels || [];
  const stages = data?.stages || [];

  if (contacts.length === 0) {
    return (
      <div className="ed-empty" data-testid="outreach-empty">
        <img src="/owlbert/main.png" alt="Owlbert" />
        <h3 className="chapter-title mt-6" style={{ fontSize: 36 }}>No contacts yet.</h3>
        <p className="chapter-sub mt-3">
          Your strategist will add prospects here as the pipeline warms up.
        </p>
      </div>
    );
  }

  // Group contacts by stage, using the workspace's stage order.
  const stageNames = stages.length
    ? stages.map((s) => s.name)
    : Array.from(new Set(contacts.map((c) => c.stage).filter(Boolean)));
  const grouped = {};
  for (const s of stageNames) grouped[s] = [];
  for (const c of contacts) {
    const key = c.stage && grouped[c.stage] ? c.stage : (stageNames[0] || "Prospects");
    if (!grouped[key]) grouped[key] = [];
    grouped[key].push(c);
  }
  const orderedStages = stageNames.length ? stageNames : Object.keys(grouped);

  return (
    <>
      <div className="mb-10" data-testid="outreach-toolbar">
        <div className="chapter-eyebrow mb-3">Outreach pipeline</div>
        <p className="chapter-sub">
          A live look at who your strategist is warming up on your behalf.
          <span className="ml-3 text-[#9ca3af] text-xs mono">
            {contacts.length} contact{contacts.length === 1 ? "" : "s"} · read-only
          </span>
        </p>
      </div>

      <div
        className="grid gap-8 overflow-x-auto pb-4 editorial"
        style={{ gridTemplateColumns: `repeat(${Math.max(orderedStages.length, 1)}, minmax(260px, 1fr))` }}
        data-testid="portal-outreach-kanban"
      >
        {orderedStages.map((stageName) => {
          const rows = grouped[stageName] || [];
          return (
            <div
              key={stageName}
              className="ed-kanban-col"
              data-testid={`portal-outreach-col-${stageName.replace(/\s+/g, "-").toLowerCase()}`}
            >
              <div className="ed-kanban-header">
                <span className="ed-kanban-header-title">{stageName}</span>
                <span className="ed-kanban-header-count">{rows.length}</span>
              </div>
              {rows.length === 0 && (
                <div className="text-[12px] text-[#9ca3af] italic px-2 py-4">Nothing here yet.</div>
              )}
              {rows.map((c) => (
                <PortalContactCard
                  key={c.id}
                  contact={c}
                  labels={labels}
                  onOpen={() => setOpenContact({ ...c, _displayStage: stageName })}
                />
              ))}
            </div>
          );
        })}
      </div>

      {openContact && (
        <PortalContactModal
          contact={openContact}
          labels={labels}
          onClose={() => setOpenContact(null)}
        />
      )}
    </>
  );
}

function PortalContactCard({ contact, labels, onOpen }) {
  const contactLabels = (contact.labels || [])
    .map((id) => labels.find((l) => l.label_id === id))
    .filter(Boolean);
  return (
    <button
      type="button"
      onClick={onOpen}
      className="ed-kanban-card w-full text-left"
      data-testid={`portal-outreach-card-${contact.id}`}
    >
      <div className="text-[14px] text-[#111827] font-semibold truncate leading-tight">
        {contact.name}
      </div>
      {contact.headline && (
        <div className="text-[11px] text-[#6b7280] truncate mt-1 leading-snug">
          {contact.headline}
        </div>
      )}
      {contactLabels.length > 0 && (
        <div className="flex items-center gap-1.5 mt-3 flex-wrap">
          {contactLabels.slice(0, 3).map((l) => (
            <span
              key={l.label_id}
              className="text-[10px] uppercase tracking-wider px-2 py-0.5 rounded-full font-semibold"
              style={{ background: `${l.color}18`, color: l.color }}
            >
              {l.name}
            </span>
          ))}
        </div>
      )}
      {contact.recommended_next_message && (
        <div className="mt-3 text-[11px] text-[#9ca3af] line-clamp-2 italic leading-relaxed">
          &ldquo;{contact.recommended_next_message}&rdquo;
        </div>
      )}
    </button>
  );
}

function PortalContactModal({ contact, labels, onClose }) {
  const contactLabels = (contact.labels || [])
    .map((id) => labels.find((l) => l.label_id === id))
    .filter(Boolean);
  const displayStage = contact.stage || contact._displayStage || null;
  const hasNarrative =
    contact.reason_targeted || contact.reason_for_outreach || contact.recommended_next_message;
  return (
    <>
      <div
        className="fixed inset-0 z-[65] bg-black/30 backdrop-blur-[2px]"
        onClick={onClose}
        data-testid="portal-contact-backdrop"
      />
      <div
        className="fixed left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 z-[70] w-[min(640px,94vw)] max-h-[86vh] overflow-y-auto portal-light bg-white rounded-2xl shadow-2xl"
        data-testid="portal-contact-modal"
      >
        <div className="px-6 pt-6 pb-4 flex items-start justify-between gap-4">
          <div className="min-w-0">
            <div className="portal-overline">Outreach contact</div>
            <h3 className="portal-serif text-2xl text-stone-900 mt-1 truncate">{contact.name}</h3>
            {contact.headline && (
              <div className="text-sm text-stone-600 mt-0.5">{contact.headline}</div>
            )}
          </div>
          <button
            onClick={onClose}
            className="portal-btn portal-btn-ghost p-2"
            data-testid="portal-contact-close"
            aria-label="Close"
          >
            <X className="w-4 h-4" />
          </button>
        </div>

        <div className="px-6 pb-6 space-y-4">
          <div className="flex items-center gap-2 flex-wrap" data-testid="portal-contact-meta">
            {displayStage && (
              <span
                className="mono text-[10px] uppercase tracking-wider px-2 py-1 rounded bg-portal-accent/10 text-portal-accent"
                data-testid="portal-contact-stage"
              >
                {displayStage}
              </span>
            )}
            {contactLabels.map((l) => (
              <span
                key={l.label_id}
                className="text-[10px] uppercase tracking-wider px-1.5 py-0.5 rounded"
                style={{ background: `${l.color}22`, color: l.color }}
              >
                {l.name}
              </span>
            ))}
            {contact.linkedin_url && (
              <a
                href={contact.linkedin_url}
                target="_blank"
                rel="noopener noreferrer"
                className="mono text-[10px] text-portal-accent hover:underline"
                data-testid="portal-contact-linkedin"
              >
                open on LinkedIn
              </a>
            )}
          </div>

          <div data-testid="portal-contact-targeted">
            <div className="portal-overline">Why we targeted them</div>
            <div className="text-sm text-stone-800 mt-1 whitespace-pre-wrap">
              {contact.reason_targeted || (
                <span className="italic text-stone-500">No notes shared yet.</span>
              )}
            </div>
          </div>

          <div data-testid="portal-contact-why-now">
            <div className="portal-overline">Why now</div>
            <div className="text-sm text-stone-800 mt-1 whitespace-pre-wrap">
              {contact.reason_for_outreach || (
                <span className="italic text-stone-500">No notes shared yet.</span>
              )}
            </div>
          </div>

          <div data-testid="portal-contact-next-message">
            <div className="portal-overline">Recommended next message</div>
            <div className="text-sm text-stone-800 mt-1 whitespace-pre-wrap p-3 rounded-lg bg-stone-50">
              {contact.recommended_next_message || (
                <span className="italic text-stone-500">Your strategist hasn&apos;t drafted the next touch yet.</span>
              )}
            </div>
          </div>

          {!hasNarrative && (
            <p className="text-[11px] italic text-stone-500 mt-2">
              Details will fill in as your strategist works this contact.
            </p>
          )}
        </div>
      </div>
    </>
  );
}

// ---------------------------------------------------------------------------
// Content Board — Kanban + Notion-style alternate views (Gallery / Table / Timeline)
// with a Day / Week / Month period picker (defaults to current week).
// ---------------------------------------------------------------------------
const BOARD_VIEW_KEY = "ll_portal_board_view";
const BOARD_PERIOD_KEY = "ll_portal_board_period";
const BOARD_STATUS_META = {
  all:       { label: "All",       fg: "#1B1E28", bg: "rgba(27,30,40,0.06)",  ring: "rgba(27,30,40,0.14)"  },
  draft:     { label: "Draft",     fg: "#4B5563", bg: "rgba(107,114,128,0.10)", ring: "rgba(107,114,128,0.25)" },
  review:    { label: "In review", fg: "#B45309", bg: "rgba(217,119,6,0.10)",   ring: "rgba(217,119,6,0.30)"   },
  published: { label: "Published", fg: "#0E7A56", bg: "rgba(14,122,86,0.10)",   ring: "rgba(14,122,86,0.30)"   },
};
const STATUS_KEYS = ["all", "draft", "review", "published"];
const PERIOD_META = {
  day:   { label: "Day" },
  week:  { label: "Week" },
  month: { label: "Month" },
};

// Pull the date to use for range filtering. Falls through: scheduled → published → created.
function postDateOf(p) {
  const s = p.due_date || p.published_at || p.created_at;
  return s ? new Date(s) : null;
}

function periodRange(period, anchor) {
  const d = new Date(anchor);
  d.setHours(0, 0, 0, 0);
  if (period === "day") {
    const end = new Date(d); end.setHours(23, 59, 59, 999);
    return [d, end];
  }
  if (period === "month") {
    const start = new Date(d.getFullYear(), d.getMonth(), 1);
    const end = new Date(d.getFullYear(), d.getMonth() + 1, 0, 23, 59, 59, 999);
    return [start, end];
  }
  // Week — ISO, Monday-anchored.
  const dow = d.getDay();
  const backToMon = (dow + 6) % 7;
  const start = new Date(d); start.setDate(d.getDate() - backToMon);
  const end = new Date(start); end.setDate(start.getDate() + 6); end.setHours(23, 59, 59, 999);
  return [start, end];
}

function shiftAnchor(period, anchor, dir) {
  const d = new Date(anchor);
  if (period === "day") d.setDate(d.getDate() + dir);
  else if (period === "week") d.setDate(d.getDate() + 7 * dir);
  else d.setMonth(d.getMonth() + dir);
  return d;
}

function labelRange(period, start, end) {
  const short = { month: "short", day: "numeric" };
  if (period === "day") {
    return start.toLocaleDateString(undefined, { weekday: "long", month: "short", day: "numeric" });
  }
  if (period === "month") {
    return start.toLocaleDateString(undefined, { month: "long", year: "numeric" });
  }
  const sameMonth = start.getMonth() === end.getMonth();
  const left = start.toLocaleDateString(undefined, short);
  const right = sameMonth
    ? end.getDate()
    : end.toLocaleDateString(undefined, short);
  return `${left} – ${right}`;
}

function ContentBoard({ posts, mode, onOpenPost, onDropMove, voiceProfiles, voiceFilter, onVoiceFilter }) {
  const [view, setView] = useState(() => localStorage.getItem(BOARD_VIEW_KEY) || "kanban");
  const [period, setPeriod] = useState(() => localStorage.getItem(BOARD_PERIOD_KEY) || "week");
  const [anchor, setAnchor] = useState(() => new Date());
  const [statusFilter, setStatusFilter] = useState("all");

  useEffect(() => { localStorage.setItem(BOARD_VIEW_KEY, view); }, [view]);
  useEffect(() => { localStorage.setItem(BOARD_PERIOD_KEY, period); }, [period]);

  const [rangeStart, rangeEnd] = useMemo(() => periodRange(period, anchor), [period, anchor]);

  // Posts within the active date range + voice filter.
  const rangedPosts = useMemo(() => {
    return posts.filter((p) => {
      if (voiceFilter && p.voice_profile_id !== voiceFilter) return false;
      // Posts awaiting the client's review are pending action — never hide them
      // behind the date window, otherwise the client can't find what to review.
      if ((p.status || "").toLowerCase() === "review") return true;
      const d = postDateOf(p);
      if (!d) return true;                   // include date-less posts so nothing "disappears"
      return d >= rangeStart && d <= rangeEnd;
    });
  }, [posts, rangeStart, rangeEnd, voiceFilter]);

  // Fresh status buckets computed from the ranged set.
  const groupedInRange = useMemo(() => {
    const out = { draft: [], review: [], published: [] };
    for (const p of rangedPosts) {
      const k = (p.status || "draft").toLowerCase();
      if (out[k]) out[k].push(p);
    }
    return out;
  }, [rangedPosts]);

  const statusCounts = {
    all: rangedPosts.length,
    draft: groupedInRange.draft.length,
    review: groupedInRange.review.length,
    published: groupedInRange.published.length,
  };

  const filteredPosts = useMemo(() => {
    if (statusFilter === "all") return rangedPosts;
    return rangedPosts.filter((p) => (p.status || "draft") === statusFilter);
  }, [rangedPosts, statusFilter]);

  const items = useMemo(() => filteredPosts.map(postToItem), [filteredPosts]);

  const isToday = useMemo(() => {
    const today = new Date();
    return (
      today.getFullYear() === anchor.getFullYear() &&
      today.getMonth() === anchor.getMonth() &&
      today.getDate() === anchor.getDate()
    );
  }, [anchor]);

  return (
    <div>
      <div className="flex items-center justify-between mb-4 flex-wrap gap-3">
        {voiceProfiles && voiceProfiles.length > 1 ? (
          <div className="flex items-center gap-1" data-testid="board-voice-filter">
            <button
              onClick={() => onVoiceFilter && onVoiceFilter("")}
              data-testid="board-voice-all"
              className={`text-xs font-medium px-3 py-1.5 rounded-md border transition-colors ${
                !voiceFilter
                  ? "border-portal-accent bg-portal-accent/10 text-portal-accent"
                  : "border-portal-line bg-white text-stone-600 hover:text-stone-900"
              }`}
            >
              All voices
            </button>
            {voiceProfiles.map((vp) => {
              const active = voiceFilter === vp.id;
              const c = voiceColor(vp.id);
              return (
                <button
                  key={vp.id}
                  onClick={() => onVoiceFilter && onVoiceFilter(vp.id)}
                  data-testid={`board-voice-${vp.id}`}
                  className={`text-xs font-medium px-3 py-1.5 rounded-md border transition-colors inline-flex items-center gap-1.5 ${
                    active
                      ? "border-portal-accent bg-portal-accent/10 text-portal-accent"
                      : "border-portal-line bg-white text-stone-600 hover:text-stone-900"
                  }`}
                >
                  <span className="w-2 h-2 rounded-full" style={{ background: c.swatch }} />
                  {vp.name}
                </button>
              );
            })}
          </div>
        ) : <div />}
        <ViewSwitcher view={view} onChange={setView} testid="board-view-switcher" />
      </div>

      {/* Period picker — day / week / month + prev/next + today reset */}
      <div
        className="flex items-center justify-between gap-3 mb-4 flex-wrap"
        data-testid="board-period-bar"
      >
        <div className="flex items-center gap-2">
          <button
            onClick={() => setAnchor(shiftAnchor(period, anchor, -1))}
            className="portal-btn portal-btn-ghost p-2"
            data-testid="board-period-prev"
            aria-label="Previous period"
          >
            <ChevronLeft className="w-4 h-4" />
          </button>
          <Popover>
            <PopoverTrigger asChild>
              <button
                className="portal-card px-4 py-2 inline-flex items-center gap-2 min-w-[220px] justify-center hover:border-portal-accent transition-colors cursor-pointer"
                data-testid="board-period-label"
                aria-label="Pick a date"
              >
                <Calendar className="w-3.5 h-3.5 text-portal-accent" strokeWidth={2} />
                <span className="text-sm font-semibold text-stone-900">
                  {labelRange(period, rangeStart, rangeEnd)}
                </span>
              </button>
            </PopoverTrigger>
            <PopoverContent
              align="center"
              className="w-auto p-0 bg-white border-portal-line shadow-xl rounded-xl"
              data-testid="board-period-calendar"
            >
              <CalendarPicker
                mode="single"
                selected={anchor}
                onSelect={(d) => d && setAnchor(d)}
                initialFocus
                weekStartsOn={1}
              />
            </PopoverContent>
          </Popover>
          <button
            onClick={() => setAnchor(shiftAnchor(period, anchor, 1))}
            className="portal-btn portal-btn-ghost p-2"
            data-testid="board-period-next"
            aria-label="Next period"
          >
            <ChevronRight className="w-4 h-4" />
          </button>
          {!isToday && (
            <button
              onClick={() => setAnchor(new Date())}
              className="portal-btn portal-btn-ghost inline-flex items-center gap-1"
              data-testid="board-period-today"
            >
              <RotateCcw className="w-3 h-3" /> Today
            </button>
          )}
        </div>

        <div
          className="inline-flex items-center p-1 rounded-lg bg-white/70 border border-portal-line shadow-sm"
          data-testid="board-period-switch"
        >
          {Object.entries(PERIOD_META).map(([k, m]) => {
            const active = period === k;
            return (
              <button
                key={k}
                onClick={() => setPeriod(k)}
                data-testid={`board-period-${k}`}
                className={`text-[12px] font-medium px-3 py-1.5 rounded-md transition-all ${
                  active
                    ? "bg-portal-accent/10 text-portal-accent shadow-sm"
                    : "text-stone-600 hover:text-stone-900 hover:bg-stone-100"
                }`}
              >
                {m.label}
              </button>
            );
          })}
        </div>
      </div>

      {/* Prominent status strip — always visible, acts as a filter in non-kanban views */}
      <div
        className="grid grid-cols-2 sm:grid-cols-4 gap-2 mb-5"
        data-testid="board-status-strip"
      >
        {STATUS_KEYS.map((k) => {
          const meta = BOARD_STATUS_META[k];
          const count = statusCounts[k];
          const active = view !== "kanban" && statusFilter === k;
          const clickable = view !== "kanban";
          return (
            <button
              key={k}
              disabled={!clickable}
              onClick={() => clickable && setStatusFilter(k)}
              data-testid={`board-status-${k}`}
              className={`text-left px-4 py-3 rounded-xl transition-all ${
                clickable ? "hover:-translate-y-0.5 hover:shadow-md cursor-pointer" : "cursor-default"
              }`}
              style={{
                background: meta.bg,
                border: `1px solid ${active ? meta.fg : meta.ring}`,
                boxShadow: active ? `inset 0 0 0 1px ${meta.fg}` : undefined,
              }}
            >
              <div className="mono text-[10px] uppercase tracking-wider font-semibold" style={{ color: meta.fg }}>
                {meta.label}
              </div>
              <div className="portal-serif text-3xl font-medium tabular-nums mt-0.5" style={{ color: meta.fg }}>
                {count}
              </div>
            </button>
          );
        })}
      </div>

      {rangedPosts.length === 0 ? (
        <div className="portal-card px-10 py-12 text-center">
          <FileText className="w-7 h-7 mx-auto text-stone-400 mb-3" strokeWidth={1.5} />
          <h3 className="portal-serif text-2xl text-stone-900 mb-1">
            {posts.length === 0 ? "No posts yet." : "Nothing in this range."}
          </h3>
          <p className="text-stone-600 text-sm">
            {posts.length === 0
              ? "drafts will start appearing here within your first week."
              : "try a wider window or jump back to today."}
          </p>
        </div>
      ) : view === "kanban" ? (
        <div className="grid grid-cols-1 md:grid-cols-3 gap-5">
          {COLUMNS.map((col) => (
            <KanbanColumn
              key={col.key}
              col={col}
              posts={groupedInRange[col.key] || []}
              onOpen={onOpenPost}
              onDropMove={onDropMove}
            />
          ))}
        </div>
      ) : view === "gallery" ? (
        <GalleryView items={items} onOpen={(it) => onOpenPost(it.id)} showStatus testid="board-gallery" />
      ) : view === "table" ? (
        <TableView items={items} onOpen={(it) => onOpenPost(it.id)} showStatus showMetrics={false} testid="board-table" />
      ) : (
        <TimelineView items={items} onOpen={(it) => onOpenPost(it.id)} showStatus testid="board-timeline" />
      )}
      {mode === "public" && view === "kanban" && rangedPosts.length > 0 && (
        <p className="mono text-[10px] uppercase tracking-wider text-stone-400 mt-4 text-center opacity-60">
          drag between draft ↔ in review
        </p>
      )}
    </div>
  );
}

// Convert a portal `posts` row → shared PortalItem shape used by shared views.
function postToItem(p) {
  const caption = p.post_caption || "";
  const fbCount = (p.client_feedback || []).filter((f) => !f.resolved).length;
  return {
    id: p.id || p.post_id,
    title: p.title || "",
    preview: caption.slice(0, 260),
    full_text: caption,
    date: p.due_date || p.published_at || p.created_at,
    voice_id: p.voice_profile_id,
    voice_name: p.voice_profile_name,
    image_url: p.image_url || p.attachment_url || null,
    status: p.status || "draft",
    external_url: p.published_url || null,
    badges: {
      feedback_count: fbCount,
      approved: !!p.client_approved,
      media_type: p.media_type,
    },
  };
}

// ---------------------------------------------------------------------------
function KanbanColumn({ col, posts, onOpen, onDropMove }) {
  const [hover, setHover] = useState(false);
  return (
    <div
      className={`kanban-col p-4 ${hover ? "ring-2 ring-portal-accent" : ""}`}
      onDragOver={(e) => { e.preventDefault(); setHover(true); }}
      onDragLeave={() => setHover(false)}
      onDrop={(e) => {
        e.preventDefault();
        setHover(false);
        const postId = e.dataTransfer.getData("postId");
        const fromStatus = e.dataTransfer.getData("fromStatus");
        if (postId) onDropMove(postId, fromStatus, col.key);
      }}
      data-testid={`portal-col-${col.key}`}
    >
      <div className="flex items-baseline justify-between mb-4">
        <div>
          <h3 className="portal-serif text-2xl text-stone-900">{col.label}</h3>
          <div className="mono text-[10px] uppercase tracking-wider text-stone-500 mt-0.5">{col.hint}</div>
        </div>
        <span className="mono text-xs text-stone-500">{posts.length}</span>
      </div>
      <div className="space-y-3">
        {posts.length === 0 && (
          <div className="text-stone-400 text-xs italic px-1 py-6 text-center">no posts here.</div>
        )}
        {posts.map((p) => (
          <KanbanCard key={p.id || p.post_id} post={p}
            onOpen={() => onOpen(p.id || p.post_id)}
            onDragStart={(e) => {
              e.dataTransfer.setData("postId", p.id || p.post_id);
              e.dataTransfer.setData("fromStatus", col.key);
            }}
          />
        ))}
      </div>
    </div>
  );
}

function KanbanCard({ post, onOpen, onDragStart }) {
  const imgUrl = post.image_url || post.attachment_url || null;
  const pdfUrl = post.pdf_url || null;
  const fbCount = (post.client_feedback || []).filter((f) => !f.resolved).length;
  const approved = !!post.client_approved;
  const preview = (post.post_caption || "").slice(0, 200);
  const vc = voiceColor(post.voice_profile_id);
  return (
    <div
      className="kanban-card p-4 relative"
      onClick={onOpen}
      draggable
      onDragStart={onDragStart}
      data-testid={`portal-card-${post.id || post.post_id}`}
      style={post.voice_profile_id ? { borderLeft: `3px solid ${vc.swatch}` } : undefined}
    >
      {(post.post_caption || "").length > 0 && (
        <div className="absolute top-2 right-2 z-10">
          <CopyIcon
            text={post.post_caption || ""}
            testid={`card-copy-${post.id || post.post_id}`}
            label=""
            className="bg-white/80 backdrop-blur px-1.5 py-0.5 border border-portal-line"
          />
        </div>
      )}
      {imgUrl && (
        <div className="aspect-[16/9] bg-portal-line overflow-hidden mb-3 -mx-4 -mt-4">
          <img src={imgUrl} alt="" className="w-full h-full object-cover" loading="lazy" onError={(e) => { e.currentTarget.closest("div").style.display = "none"; }} />
        </div>
      )}
      {!imgUrl && pdfUrl && (
        <div className="mb-3 flex items-center gap-2 text-xs text-stone-500">
          <FileText className="w-3.5 h-3.5" /> <span className="mono uppercase tracking-wider">carousel pdf</span>
        </div>
      )}
      {post.title && (
        <div className="portal-serif text-lg text-stone-900 leading-snug mb-1.5">{post.title}</div>
      )}
      {post.voice_profile_name && (
        <div className="mb-2">
          <VoiceChip
            voiceId={post.voice_profile_id}
            voiceName={post.voice_profile_name}
            variant="light"
            size="sm"
            testid={`portal-card-voice-${post.id || post.post_id}`}
          />
        </div>
      )}
      {preview && (
        <p className="text-[13px] text-stone-700 leading-relaxed line-clamp-3 whitespace-pre-wrap">
          {preview}{(post.post_caption || "").length > 200 ? "…" : ""}
        </p>
      )}
      <div className="flex items-center justify-between mt-3 pt-3 border-t border-portal-line">
        <div className="flex items-center gap-2">
          {approved && (
            <span className="mono text-[10px] uppercase tracking-wider text-emerald-700 inline-flex items-center gap-1">
              <CheckCircle2 className="w-3 h-3" /> approved
            </span>
          )}
          {fbCount > 0 && (
            <span className="mono text-[10px] uppercase tracking-wider text-portal-accent inline-flex items-center gap-1">
              <MessageSquarePlus className="w-3 h-3" /> {fbCount}
            </span>
          )}
        </div>
        {post.due_date && (
          <span className="mono text-[10px] text-stone-500 inline-flex items-center gap-1">
            <Calendar className="w-3 h-3" /> {(post.due_date || "").slice(0, 10)}
          </span>
        )}
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
function PostDrawer({ post, workspace, mode, clientName, onClose, onComment, onSubmitDraft, onApprove, onRequireName }) {
  const [tab, setTab] = useState("recommend"); // recommend | submit
  const [comment, setComment] = useState("");
  const [draftCaption, setDraftCaption] = useState("");
  const [draftNote, setDraftNote] = useState("");
  const [busy, setBusy] = useState(false);

  const imgUrl = post.image_url || post.attachment_url || null;
  const pdfUrl = post.pdf_url || null;
  const videoUrl = post.video_url || null;
  const linkUrl = post.attachment_link || null;
  const feedback = [...(post.client_feedback || [])].sort(
    (a, b) => new Date(b.created_at) - new Date(a.created_at)
  );

  const isPublic = mode === "public";
  const approvalEnabled = workspace?.approval_enabled !== false;
  const alreadyApproved = !!post.client_approved;

  const submitComment = async () => {
    const text = (comment || "").trim();
    if (!text) { toast.error("type a recommendation first"); return; }
    if (!clientName) { onRequireName(); return; }
    setBusy(true);
    const ok = await onComment(text);
    setBusy(false);
    if (ok) {
      setComment("");
      toast.success("recommendation sent");
    }
  };

  const submitDraft = async () => {
    const cap = draftCaption || "";
    if (!cap.trim()) { toast.error("paste your draft caption first"); return; }
    if (!clientName) { onRequireName(); return; }
    setBusy(true);
    // IMPORTANT: send the caption verbatim — preserve every newline, blank
    // line, and leading/trailing whitespace. Only the note is trimmed.
    const ok = await onSubmitDraft(cap, (draftNote || "").trim());
    setBusy(false);
    if (ok) {
      setDraftCaption("");
      setDraftNote("");
      toast.success("your draft was sent");
    }
  };

  const doApprove = async () => {
    if (!clientName) { onRequireName(); return; }
    setBusy(true);
    const ok = await onApprove();
    setBusy(false);
    if (ok) toast.success("approved — your strategist has been notified");
  };

  return (
    <>
      <div className="portal-drawer-backdrop" onClick={onClose} data-testid="portal-drawer-backdrop" />
      <aside className="portal-drawer portal-light" data-testid="portal-drawer" role="dialog">
        {/* Sticky header */}
        <div className="sticky top-0 z-10 flex items-center justify-between px-8 py-4 border-b border-portal-line bg-portal-cream/95 backdrop-blur">
          <div className="flex items-center gap-3 flex-wrap">
            <span className="mono text-[10px] uppercase tracking-wider text-stone-500">
              {post.status === "draft" ? "draft" : post.status === "review" ? "in review" : "published"}
            </span>
            {post.voice_profile_name && (
              <VoiceChip
                voiceId={post.voice_profile_id}
                voiceName={post.voice_profile_name}
                variant="light"
                size="sm"
                testid="drawer-voice-chip"
              />
            )}
            {alreadyApproved && (
              <span className="mono text-[10px] uppercase tracking-wider text-emerald-700 inline-flex items-center gap-1">
                <CheckCircle2 className="w-3 h-3" /> approved by {post.client_approved_by}
              </span>
            )}
          </div>
          <button onClick={onClose} className="portal-btn portal-btn-ghost p-2" data-testid="portal-drawer-close">
            <X className="w-4 h-4" />
          </button>
        </div>

        <div className="px-8 py-8 space-y-10 max-w-3xl mx-auto">
          {/* Title */}
          {post.title && (
            <h2 className="portal-serif text-4xl text-stone-900 leading-tight" data-testid="drawer-title">
              {post.title}
            </h2>
          )}

          {/* Media — inline previews so the client never has to download */}
          {imgUrl && (
            <div className="bg-white rounded-2xl p-3" style={{ boxShadow: "0 1px 2px rgba(17,24,39,0.05)" }}>
              <img
                src={imgUrl}
                alt={post.title || "post image"}
                className="w-full h-auto max-h-[78vh] object-contain mx-auto block rounded-xl"
                data-testid="drawer-image"
                onError={(e) => { e.currentTarget.closest("div").style.display = "none"; }}
              />
            </div>
          )}

          {videoUrl && (
            <div
              className="bg-black rounded-2xl overflow-hidden"
              style={{ boxShadow: "0 1px 2px rgba(17,24,39,0.05)" }}
              data-testid="drawer-video-wrap"
            >
              <video
                src={videoUrl}
                controls
                playsInline
                className="w-full max-h-[78vh] block"
                data-testid="drawer-video"
              >
                Your browser can&apos;t play this video.{" "}
                <a href={videoUrl} target="_blank" rel="noopener noreferrer" className="underline">
                  Open in new tab
                </a>
              </video>
            </div>
          )}

          {pdfUrl && (
            <div
              className="bg-white rounded-2xl overflow-hidden"
              style={{ boxShadow: "0 1px 2px rgba(17,24,39,0.05)" }}
              data-testid="drawer-pdf-wrap"
            >
              <div className="flex items-center justify-between px-4 py-3">
                <div className="flex items-center gap-2">
                  <FileText className="w-4 h-4 text-portal-accent" />
                  <span className="portal-overline">Carousel PDF</span>
                </div>
                <a
                  href={pdfUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="mono text-[11px] text-portal-accent hover:underline inline-flex items-center gap-1"
                  data-testid="drawer-pdf-open-new"
                >
                  open full page ↗
                </a>
              </div>
              <iframe
                src={pdfUrl}
                title="Carousel PDF"
                className="w-full block"
                style={{ height: "min(78vh, 720px)", border: 0 }}
                data-testid="drawer-pdf"
              />
            </div>
          )}

          {/* Caption — formatted, readable, verbatim (newlines + blank lines preserved) */}
          <div data-testid="drawer-caption-section">
            <div className="flex items-center justify-between mb-2">
              <div className="portal-overline">post caption</div>
              {(post.post_caption || "").length > 0 && (
                <CopyIcon
                  text={post.post_caption || ""}
                  testid="drawer-caption-copy"
                  label="copy whole caption"
                />
              )}
            </div>
            <article className="prose-portal" data-testid="drawer-caption">
              <pre className="whitespace-pre-wrap break-words font-sans text-[15.5px] leading-[1.8] text-stone-800 m-0">
{post.post_caption || ""}
              </pre>
            </article>
          </div>

          {linkUrl && (
            <a href={linkUrl} target="_blank" rel="noopener noreferrer"
              className="mono text-xs text-portal-accent hover:underline">
              external reference ↗
            </a>
          )}

          {/* Actions */}
          {isPublic && (
            <div className="border-t border-portal-line pt-8 pb-2">
              <div className="flex items-center justify-between flex-wrap gap-3 mb-5">
                <div>
                  <div className="portal-overline">your move</div>
                  <h3 className="portal-serif text-2xl text-stone-900 mt-1">
                    Recommend a change, submit your own draft{approvalEnabled ? ", or approve it" : ""}.
                  </h3>
                </div>
                {approvalEnabled && (
                  <button
                    className="portal-btn portal-btn-primary inline-flex items-center gap-2"
                    onClick={doApprove}
                    disabled={busy || alreadyApproved}
                    data-testid="drawer-approve-btn"
                  >
                    <CheckCircle2 className="w-4 h-4" />
                    {alreadyApproved ? "approved" : "approve this post"}
                  </button>
                )}
              </div>

              {/* Tabs */}
              <div className="flex gap-1 border-b border-portal-line mb-5">
                <DrawerTab active={tab === "recommend"} onClick={() => setTab("recommend")}>
                  <MessageSquarePlus className="w-3.5 h-3.5" /> Recommend change
                </DrawerTab>
                <DrawerTab active={tab === "submit"} onClick={() => setTab("submit")}>
                  <FilePlus2 className="w-3.5 h-3.5" /> Submit your draft
                </DrawerTab>
              </div>

              {tab === "recommend" && (
                <div className="space-y-3">
                  <textarea
                    className="portal-textarea"
                    placeholder="e.g. lead with the data point. soften the close. cut paragraph 3."
                    value={comment}
                    onChange={(e) => setComment(e.target.value)}
                    rows={4}
                    data-testid="drawer-comment-textarea"
                  />
                </div>
              )}

              {tab === "submit" && (
                <div className="space-y-3">
                  <textarea
                    className="portal-textarea"
                    placeholder="paste or write your version of the caption here…"
                    value={draftCaption}
                    onChange={(e) => setDraftCaption(e.target.value)}
                    rows={10}
                    data-testid="drawer-draft-textarea"
                  />
                  <input
                    className="portal-input"
                    placeholder="quick note for your strategist (optional)"
                    value={draftNote}
                    onChange={(e) => setDraftNote(e.target.value)}
                    data-testid="drawer-draft-note"
                  />
                </div>
              )}
            </div>
          )}

          {/* Feedback thread */}
          {feedback.length > 0 && (
            <div className="border-t border-portal-line pt-8 pb-28">
              <div className="portal-overline mb-3">activity · {feedback.length}</div>
              <div className="space-y-3" data-testid="drawer-feedback-thread">
                {feedback.map((f) => (
                  <FeedbackBubble key={f.id} item={f} />
                ))}
              </div>
            </div>
          )}
          {feedback.length === 0 && isPublic && <div className="pb-28" />}
        </div>

        {/* Sticky action footer */}
        {isPublic && (
          <div className="sticky bottom-0 left-0 right-0 border-t border-portal-line bg-portal-cream/95 backdrop-blur px-8 py-3 z-10 flex justify-end gap-2">
            {tab === "recommend" ? (
              <button
                className="portal-btn portal-btn-primary inline-flex items-center gap-2"
                onClick={submitComment}
                disabled={busy}
                data-testid="drawer-send-comment-btn"
              >
                <Send className="w-3.5 h-3.5" /> send recommendation
              </button>
            ) : (
              <button
                className="portal-btn portal-btn-primary inline-flex items-center gap-2"
                onClick={submitDraft}
                disabled={busy}
                data-testid="drawer-send-draft-btn"
              >
                <Sparkles className="w-3.5 h-3.5" /> send your draft
              </button>
            )}
          </div>
        )}
      </aside>
    </>
  );
}

function DrawerTab({ active, onClick, children }) {
  return (
    <button
      onClick={onClick}
      className={`px-4 py-2.5 text-[13px] inline-flex items-center gap-1.5 border-b-2 transition-colors ${
        active
          ? "border-portal-accent text-portal-accent"
          : "border-transparent text-stone-600 hover:text-stone-900"
      }`}
    >
      {children}
    </button>
  );
}

function FeedbackBubble({ item }) {
  const isDraft = item.kind === "draft_submission";
  const isApproval = item.kind === "approval";
  const cls = isApproval ? "fb-bubble fb-bubble-approval"
    : isDraft ? "fb-bubble fb-bubble-draft" : "fb-bubble";
  return (
    <div className={cls}>
      <div className="flex items-center justify-between mb-1.5">
        <div className="text-sm font-medium text-stone-900">
          {item.author_name}
          <span className="mono text-[10px] uppercase tracking-wider text-stone-500 ml-2">
            {isApproval ? "approved" : isDraft ? "submitted a draft" : "recommended a change"}
          </span>
        </div>
        <div className="mono text-[10px] text-stone-500">
          {new Date(item.created_at).toLocaleDateString()}
        </div>
      </div>
      {item.content && (
        <p className="text-sm text-stone-800 whitespace-pre-wrap leading-relaxed">{item.content}</p>
      )}
      {isDraft && item.caption && (
        <div className="mt-2 bg-portal-cream border border-portal-line p-3">
          <div className="flex items-center justify-between mb-1.5">
            <div className="mono text-[10px] uppercase tracking-wider text-stone-500">their version</div>
            <CopyIcon
              text={item.caption}
              testid={`fb-copy-${item.id}`}
              label="copy"
            />
          </div>
          <pre className="whitespace-pre-wrap break-words font-sans text-[13.5px] leading-[1.7] text-stone-800 m-0">
{item.caption}
          </pre>
        </div>
      )}
    </div>
  );
}

function NamePrompt({ current, onSave, onCancel }) {
  const [v, setV] = useState(current || "");
  return (
    <>
      <div className="portal-drawer-backdrop" onClick={onCancel} />
      <div className="fixed inset-0 z-[62] flex items-center justify-center p-6 portal-light">
        <div className="portal-card w-full max-w-md p-8">
          <div className="portal-overline mb-3">who are we talking to</div>
          <h3 className="portal-serif text-3xl text-stone-900 mb-4">Your name</h3>
          <p className="text-sm text-stone-600 mb-5">
            so your strategist knows who&apos;s giving the feedback. shown on every comment and draft you submit.
          </p>
          <input
            className="portal-input mb-5"
            placeholder="e.g. Angad"
            value={v}
            onChange={(e) => setV(e.target.value)}
            autoFocus
            data-testid="portal-name-input"
          />
          <div className="flex justify-end gap-2">
            <button className="portal-btn" onClick={onCancel}>Cancel</button>
            <button
              className="portal-btn portal-btn-primary"
              onClick={() => v.trim() && onSave(v.trim())}
              disabled={!v.trim()}
              data-testid="portal-name-save"
            >
              save
            </button>
          </div>
        </div>
      </div>
    </>
  );
}

// ---------------------------------------------------------------------------
function fmtNum(n) {
  if (n === null || n === undefined) return 0;
  if (n >= 1000000) return (n / 1000000).toFixed(1) + "M";
  if (n >= 1000) return (n / 1000).toFixed(1) + "k";
  return n;
}

// Inline copy affordance. Tiny icon-only button, copies the EXACT string
// passed (newlines, blank lines, Unicode bold all preserved verbatim).
export function CopyIcon({ text, label = "copy", testid, size = 14, className = "" }) {
  const [copied, setCopied] = useState(false);
  const onClick = async (e) => {
    e.stopPropagation();
    e.preventDefault();
    try {
      await navigator.clipboard.writeText(text || "");
      setCopied(true);
      toast.success("copied");
      setTimeout(() => setCopied(false), 1400);
    } catch {
      toast.error("could not copy");
    }
  };
  return (
    <button
      type="button"
      onClick={onClick}
      title={label}
      data-testid={testid || "copy-icon"}
      className={`inline-flex items-center gap-1 text-stone-500 hover:text-stone-900 transition-colors ${className}`}
    >
      {copied
        ? <Check style={{ width: size, height: size }} />
        : <Copy style={{ width: size, height: size }} />}
      <span className="mono text-[10px] uppercase tracking-wider">
        {copied ? "copied" : label}
      </span>
    </button>
  );
}

